import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../sporttia/all';
import { PosPaymentParams } from '../../types/payment';
import {
    DataphoneOperation,
    DataphoneOperationStatuses,
} from '../../types/models/DataphoneOperation';
import useSipayDataphoneSale from './useSipayDataphoneSale';

type SipayPaymentDialogProps = {
    paymentData: PosPaymentParams;
    onClose: () => void;
    onSuccess?: (ticket?: unknown, returnParams?: unknown) => void;
};

// Resultados en los que no se ha cobrado nada y el pago sigue pendiente
const NOT_CHARGED_TITLES: Partial<
    Record<DataphoneOperation['status'], string>
> = {
    [DataphoneOperationStatuses.declined]: 'sipayPaymentDialog.declined',
    [DataphoneOperationStatuses.cancelled]: 'sipayPaymentDialog.cancelled',
    [DataphoneOperationStatuses.rejected]: 'sipayPaymentDialog.rejected',
    [DataphoneOperationStatuses.notCharged]: 'sipayPaymentDialog.notCharged',
};

/**
 * Cobro con el datáfono conectado de Sipay (PayGate). Sporttia manda la venta
 * al terminal y el resultado llega por SSE. Cerrar el diálogo no cancela la
 * venta: solo se cancela en la pantalla del datáfono.
 */
export default function SipayPaymentDialog({
    paymentData,
    onClose,
    onSuccess,
}: SipayPaymentDialogProps) {
    const { t } = useTranslation();
    const { launchError, operation, paid, notConfirmed } =
        useSipayDataphoneSale(paymentData.idTpv, () =>
            onSuccess?.(undefined, paymentData.returnParams),
        );

    // Si el rechazo es nuestro llega su clave de traducción; si no, el texto
    // del banco tal cual
    const declineMessage = operation?.declineReason
        ? t(operation.declineReason)
        : operation?.declineMessage;
    const notChargedTitle = operation && NOT_CHARGED_TITLES[operation.status];

    const renderContent = () => {
        if (launchError) {
            return (
                <>
                    <ErrorOutlineIcon color="error" fontSize="large" />
                    <Typography color="error">{launchError}</Typography>
                </>
            );
        }

        if (!operation) {
            return (
                <>
                    <CircularProgress />
                    <Typography>{t('sipayPaymentDialog.sending')}</Typography>
                </>
            );
        }

        if (paid) {
            return (
                <>
                    <CheckCircleIcon color="success" fontSize="large" />
                    <Typography>{t('sipayPaymentDialog.paid')}</Typography>
                </>
            );
        }

        if (notConfirmed) {
            return (
                <>
                    <WarningAmberIcon color="warning" fontSize="large" />
                    <Typography>
                        {t('sipayPaymentDialog.notConfirmed')}
                    </Typography>
                </>
            );
        }

        if (operation.status === DataphoneOperationStatuses.approved) {
            return (
                <>
                    <CircularProgress />
                    <Typography>{t('sipayPaymentDialog.approved')}</Typography>
                    {operation.cardMasked && (
                        <Typography variant="body2" color="text.secondary">
                            {t('sipayPaymentDialog.card', {
                                card: operation.cardMasked,
                            })}
                        </Typography>
                    )}
                </>
            );
        }

        if (operation.status === DataphoneOperationStatuses.unknown) {
            return (
                <>
                    <WarningAmberIcon color="warning" fontSize="large" />
                    <Typography>{t('sipayPaymentDialog.unknown')}</Typography>
                </>
            );
        }

        if (notChargedTitle) {
            return (
                <>
                    <ErrorOutlineIcon color="error" fontSize="large" />
                    <Typography>{t(notChargedTitle)}</Typography>
                    {declineMessage && (
                        <Typography color="error">{declineMessage}</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {t('sipayPaymentDialog.notChargedHint')}
                    </Typography>
                </>
            );
        }

        return (
            <>
                <CircularProgress />
                <Typography>
                    {t('sipayPaymentDialog.waitingForCard')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('sipayPaymentDialog.cancelOnTerminal')}
                </Typography>
            </>
        );
    };

    return (
        <SttDialog
            open
            title={t('posPaymentDialog.title')}
            maxWidth="sm"
            onClose={onClose}
            content={
                <Box className="flex flex-col items-center gap-4 text-center">
                    {renderContent()}
                </Box>
            }
        />
    );
}
